import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useAccessControl } from "@/hooks/useAccessControl";
import { requestPlan } from "../concierge/api";
import { applyPlan } from "../concierge/applyPlan";
import { createOwnPortfolio, type OwnPortfolioResult } from "../ownPortfolio";
import { resolveSecondTab } from "../templates/secondTab";
import {
  finishOnboardingTabs,
  lastImportedTabId,
} from "../templates/finishTabs";
import { buildLayoutExportData } from "../templates/packLayout";
import { applyLayoutData } from "../templates/applyTemplate";
import PlanPreview from "../concierge/PlanPreview";
import { conciergeFailureReason, trackWelcomeSetup } from "../analytics";
import { CTA_PRIMARY } from "../ui/theme";
import ConciergeWorking from "../ui/ConciergeWorking";
import type { ProvisioningPlan } from "../concierge/types";
import type { OnboardingAnswers } from "../types";

interface StepConciergeProps {
  answers: OnboardingAnswers;
  onComplete: (appliedTemplateId: string) => void;
  /** Fall back to the manual template path. */
  onFallback: () => void;
}

type Phase = "loading" | "preview" | "applying" | "error";

export default function StepConcierge({
  answers,
  onComplete,
  onFallback,
}: StepConciergeProps) {
  const { hasAccessToWidget } = useAccessControl();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>("loading");
  const [plan, setPlan] = useState<ProvisioningPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState(0);
  const requestedRef = useRef(false);
  /**
   * Held across renders because it is started when the request goes out and
   * awaited when the user applies — two different events.
   */
  const ownPortfolioRef = useRef<Promise<OwnPortfolioResult | null>>(
    Promise.resolve(null)
  );

  // Advance the stage list while the request is in flight (typical 9-13s).
  useEffect(() => {
    if (phase !== "loading") return;
    const timers = [
      setTimeout(() => setStage(1), 3200),
      setTimeout(() => setStage(2), 6800),
    ];
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    trackWelcomeSetup("concierge_requested");
    // Created before the request so the prompt's "already saved" is true by
    // the time the plan comes back and the user applies it.
    ownPortfolioRef.current = createOwnPortfolio(answers.tickers ?? []);
    /**
     * Tickers first, and stated as already handled.
     *
     * They are the one unambiguous thing the user gave us — prose has to be
     * interpreted, a ticker list does not. Leading with them, and saying the
     * watchlist already exists, stops the model spending its plan
     * re-deriving names we were handed and lets it build around them instead.
     */
    const own = answers.tickers?.length
      ? `I already hold: ${answers.tickers.join(", ")} ` +
        `(these are already saved as a watchlist — do not recreate them, ` +
        `build around them).`
      : null;
    const query = [
      own,
      answers.freeText?.trim(),
      answers.assetClasses.length
        ? `(asset classes: ${answers.assetClasses.join(", ")})`
        : null,
    ]
      .filter(Boolean)
      .join(" ");
    requestPlan(query, "onboarding")
      .then((response) => {
        setPlan(response.plan);
        setPhase("preview");
      })
      .catch((err) => {
        console.error("[concierge] plan request failed:", err);
        trackWelcomeSetup("concierge_failed", conciergeFailureReason(err));
        setError(
          err?.response?.data?.detail ??
            "We couldn't build a plan right now — pick a template instead."
        );
        setPhase("error");
      });
  }, [answers]);

  const handleApply = async () => {
    if (!plan) return;
    setPhase("applying");
    try {
      const own = await ownPortfolioRef.current;
      const result = await applyPlan(plan, {
        hasAccessToWidget,
        highlightWatchlistIds: own ? [own.id] : undefined,
      });

      /**
       * Same bonus tab the template route builds, derived from entitlements
       * rather than from the plan — which is why the concierge gets it with no
       * change to the plan schema and no extra tokens per request. Swallowed
       * on failure: the setup the user asked for is already applied.
       */
      const primaryTabId = lastImportedTabId();

      const second = resolveSecondTab(answers, "concierge", hasAccessToWidget);
      if (second) {
        try {
          applyLayoutData(
            buildLayoutExportData(second.name, second.widgets),
            {
              hasAccessToWidget,
              highlightWatchlistIds: own ? [own.id] : undefined,
              repackOnDrop: true,
            }
          );
        } catch (error) {
          console.error("[concierge] second dashboard skipped:", error);
        }
      }
      finishOnboardingTabs(primaryTabId);

      queryClient.invalidateQueries({ queryKey: ["get-portfolios"] });
      queryClient.invalidateQueries({ queryKey: ["tagsWatchlists"] });
      queryClient.invalidateQueries({ queryKey: ["headlineQueries"] });
      // No concierge-specific success event: onComplete reports
      // welcome_setup/completed with detail "concierge".
      toast.success(
        result.tabTitle ? `"${result.tabTitle}" is ready` : "Your setup is ready"
      );
      onComplete("concierge");
    } catch (err) {
      console.error("[concierge] apply failed:", err);
      trackWelcomeSetup("concierge_failed", "apply");
      toast.error("Couldn't apply that — anything created is in Watchlists.");
      setPhase("preview");
    }
  };

  if (phase === "loading") {
    return <ConciergeWorking stage={stage} />;
  }

  if (phase === "error") {
    return (
      <div className="py-6">
        <p className="text-sm text-gray-300">{error}</p>
        <button type="button" onClick={onFallback} className={`${CTA_PRIMARY} mt-5`}>
          Pick a template
        </button>
      </div>
    );
  }

  return (
    <div>
      {plan && <PlanPreview plan={plan} onChange={setPlan} />}

      <div className="mt-7">
        <button
          type="button"
          onClick={handleApply}
          disabled={phase === "applying"}
          className={CTA_PRIMARY}
        >
          {phase === "applying" ? "Setting up…" : "Apply this setup"}
        </button>
        {/* Demoted from a peer button. Side by side, "Use a template" read as
            an equal choice and invited the user to throw away the setup they
            had just asked for — it is an escape hatch, not an alternative. */}
        <p className="mt-3 text-xs text-gray-500">
          Not what you meant?{" "}
          <button
            type="button"
            onClick={() => {
              // They saw a plan and chose not to use it. The error screen's
              // "Pick a template" is not reported: concierge_failed already
              // says why they are leaving.
              trackWelcomeSetup("concierge_rejected");
              onFallback();
            }}
            disabled={phase === "applying"}
            className="cursor-pointer border-none bg-transparent p-0 text-xs text-gray-400 underline underline-offset-2 transition-colors hover:text-gray-200 disabled:cursor-not-allowed"
          >
            Pick a starter template instead
          </button>
        </p>
      </div>
    </div>
  );
}
